import {
  formatVenueSearchError,
  getPlacePhotoUrl,
  hasGoogleMapsApiKey,
  loadPlacesLibrary,
} from './googleMapsLoader'

const MIN_QUERY_LEN = 2
const MAX_RESULTS = 6

// Open Location Code fragment (e.g. "VG6J+7XQ") — often returned by Google
// as the leading segment of secondary_text / formatted_address.
const PLUS_CODE_RE =
  /\s*[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3}(?:\s+[23456789CFGHJMPQRVWX]{2,3})?\s*,?\s*/gi

function stripPlusCodeFromAddress(address) {
  if (!address) return ''
  return String(address)
    .replace(PLUS_CODE_RE, ' ')
    .replace(/,\s*,/g, ',')
    .replace(/^[\s,]+|[\s,]+$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

let placesService = null

function getPlacesService() {
  if (!placesService) {
    const div = document.createElement('div')
    placesService = new window.google.maps.places.PlacesService(div)
  }
  return placesService
}

function extractCountryCode(components) {
  const match = (components || []).find((c) => c.types?.includes('country'))
  return match?.short_name || ''
}

function legacyPredictionToVenue(prediction) {
  const rawAddress =
    prediction.structured_formatting?.secondary_text || prediction.description || ''
  const formattedAddress =
    stripPlusCodeFromAddress(rawAddress) ||
    stripPlusCodeFromAddress(prediction.description || '')

  return {
    placeId: prediction.place_id,
    name: prediction.structured_formatting?.main_text || prediction.description || '',
    formattedAddress,
    lat: null,
    lng: null,
    countryCode: '',
    photoUrl: null,
    rating: null,
    ratingCount: null,
  }
}

function legacyPlaceToVenue(place) {
  const lat = place.geometry?.location?.lat?.()
  const lng = place.geometry?.location?.lng?.()
  const photo = place.photos?.[0]

  return {
    placeId: place.place_id || '',
    name: place.name || '',
    formattedAddress: stripPlusCodeFromAddress(place.formatted_address || ''),
    lat: typeof lat === 'number' ? lat : null,
    lng: typeof lng === 'number' ? lng : null,
    countryCode: extractCountryCode(place.address_components),
    photoUrl: photo ? getPlacePhotoUrl(photo, 200) : null,
    rating: typeof place.rating === 'number' ? place.rating : null,
    ratingCount:
      typeof place.user_ratings_total === 'number' ? place.user_ratings_total : null,
  }
}

function stripInternalFields(venue) {
  const { _placePrediction, ...rest } = venue
  return rest
}

function getLegacyAutocompletePredictions(input, countryCode = null) {
  return loadPlacesLibrary().then(
    () =>
      new Promise((resolve, reject) => {
        const service = new window.google.maps.places.AutocompleteService()
        const request = { input, types: ['establishment'] }

        if (countryCode && countryCode.length === 2) {
          request.componentRestrictions = { country: countryCode.toLowerCase() }
        }

        service.getPlacePredictions(request, (predictions, status) => {
          const { PlacesServiceStatus } = window.google.maps.places
          if (status === PlacesServiceStatus.OK) {
            resolve(predictions || [])
            return
          }
          if (status === PlacesServiceStatus.ZERO_RESULTS) {
            resolve([])
            return
          }
          reject(new Error(status))
        })
      }),
  )
}

/**
 * Search venues via legacy Places Autocomplete (Maps JavaScript API).
 * Uses the legacy Places API already enabled on the Statbox GCP project.
 */
export async function searchVenues(query, countryCode = null) {
  const q = String(query || '').trim()
  if (q.length < MIN_QUERY_LEN) return []

  if (!hasGoogleMapsApiKey()) {
    console.warn('Venue search unavailable: missing VITE_GOOGLE_MAPS_API_KEY')
    return []
  }

  try {
    let predictions = await getLegacyAutocompletePredictions(q, countryCode)

    if (predictions.length === 0 && countryCode) {
      predictions = await getLegacyAutocompletePredictions(q, null)
    }

    return predictions.slice(0, MAX_RESULTS).map(legacyPredictionToVenue)
  } catch (err) {
    console.error('Google Places search failed.', err)
    throw err
  }
}

/**
 * Fetch full venue details after the user picks a suggestion.
 */
export async function fetchVenueDetails(placeId) {
  if (!placeId || String(placeId).startsWith('manual_')) {
    throw new Error('Invalid place id')
  }

  if (!hasGoogleMapsApiKey()) {
    throw new Error('Google Maps API key required')
  }

  await loadPlacesLibrary()

  return new Promise((resolve, reject) => {
    getPlacesService().getDetails(
      {
        placeId,
        fields: [
          'place_id',
          'name',
          'formatted_address',
          'geometry',
          'photos',
          'rating',
          'user_ratings_total',
          'address_components',
        ],
      },
      (place, status) => {
        const { PlacesServiceStatus } = window.google.maps.places
        if (status !== PlacesServiceStatus.OK || !place) {
          reject(new Error(status || 'Place details failed'))
          return
        }
        resolve(stripInternalFields(legacyPlaceToVenue(place)))
      },
    )
  })
}

export { stripInternalFields, formatVenueSearchError, stripPlusCodeFromAddress }
